import { STORE_TIMEZONE } from "./timezone";

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date, a time of day, and no zone: `2026-09-30T10:00`, `2026-09-30T10:00:30`
 * or `2026-09-30T10:00:30.5`. The parts are the day, the hour, the minute, the
 * second and the fraction of one.
 */
const ZONELESS =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/;

/**
 * Whether a filter value is a date with no time in it: `2026-09-30`, what
 * `<input type="date">` writes. Such a value names a calendar day in the
 * store's timezone (`STORE_TIMEZONE`), not an instant, so "shipped between 30
 * Sep and 30 Sep" is the whole of that day in Kolkata (#492). Both filter
 * engines, the API's SQL and the fixture adapter's in-memory one, read a value
 * this way, which is why the test lives here beside the timezone rather than in
 * either of them.
 */
export function isDayOnly(text: string): boolean {
  return DAY_ONLY.test(text);
}

/**
 * Whether a date-only value is a day that exists. `2026-02-31` is not:
 * JavaScript's `Date.parse` rolls it over to March and Postgres raises on it,
 * and a filter asking for it should match nothing rather than either.
 */
export function isCalendarDay(text: string): boolean {
  const parts = DAY_ONLY.exec(text);
  if (!parts) return false;

  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const real = new Date(Date.UTC(year, month - 1, day));
  return (
    real.getUTCFullYear() === year &&
    real.getUTCMonth() === month - 1 &&
    real.getUTCDate() === day
  );
}

/**
 * Whether a value is written as a date and a time with no zone, a real one or
 * not: `2026-02-31T10:00` is.
 *
 * For what reads a filter value and must not pass it on as an instant. Such a
 * value is the store's wall clock or it is nothing, and an impossible one is
 * nothing: `Date.parse` rolls it over to March and Postgres raises on it.
 */
export function looksZoneless(text: string): boolean {
  return ZONELESS.test(text);
}

/**
 * Whether a filter value is a date and a time with no zone in it:
 * `2026-09-30T10:00`, what `<input type="datetime-local">` writes (#519).
 *
 * Such a value is a wall-clock time, and the wall it is read off is the
 * store's (`STORE_TIMEZONE`): the table shows the same column in that zone, so
 * "after 10:00" has to mean 10:00 there, not 10:00 UTC, which is half past
 * three in the afternoon in Kolkata. A value that carries a `Z` or an offset
 * names an instant and is not this. The day has to exist and the time has to be
 * a time of day, as `isCalendarDay` insists for a day alone.
 */
export function isZonelessDateTime(text: string): boolean {
  const parts = ZONELESS.exec(text);
  if (!parts) return false;

  const [, day = "", hour = "", minute = "", second] = parts;
  return (
    isCalendarDay(day) &&
    Number(hour) <= 23 &&
    Number(minute) <= 59 &&
    (second === undefined || Number(second) <= 59)
  );
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** The wall clock `utcMs` shows in `timeZone`, read back as if it were UTC. */
function wallClockAsUtc(utcMs: number, timeZone: string): number {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, formatter);
  }

  const read: Record<string, number> = {};
  for (const part of formatter.formatToParts(new Date(utcMs))) {
    if (part.type !== "literal") read[part.type] = Number(part.value);
  }
  return Date.UTC(
    read.year ?? 0,
    (read.month ?? 1) - 1,
    read.day ?? 1,
    read.hour ?? 0,
    read.minute ?? 0,
    read.second ?? 0,
  );
}

/** How far ahead of UTC `timeZone`'s clock is at `utcMs`, in milliseconds. */
function offsetAt(utcMs: number, timeZone: string): number {
  return wallClockAsUtc(utcMs, timeZone) - Math.floor(utcMs / 1000) * 1000;
}

/**
 * The instant a zone-less date and time names in `timeZone`, as milliseconds
 * since the epoch; null when the value is not one (`isZonelessDateTime`).
 *
 * What `at time zone` does in Postgres, for the fixture adapter's in-memory
 * filter and for validation, which have no Postgres to ask. The offset is read
 * twice: once at the wall-clock time taken as UTC, and again at the instant that
 * gave, so a time on the far side of a daylight-saving change is corrected. The
 * store's zone has none, but the constant is not meant to stay that way.
 */
export function zonelessInstant(
  text: string,
  timeZone: string = STORE_TIMEZONE,
): number | null {
  if (!isZonelessDateTime(text)) return null;
  const parts = ZONELESS.exec(text);
  if (!parts) return null;

  const [year = 0, month = 1, day = 1] = (parts[1] ?? "")
    .split("-")
    .map(Number);
  const fraction = parts[5] ? Number(`0.${parts[5]}`) : 0;
  const wall = Date.UTC(
    year,
    month - 1,
    day,
    Number(parts[2]),
    Number(parts[3]),
    Number(parts[4] ?? 0),
    Math.floor(fraction * 1000),
  );

  const first = wall - offsetAt(wall, timeZone);
  return wall - offsetAt(first, timeZone);
}

/** The day after `day` (`2026-09-30` → `2026-10-01`), for a day that exists. */
function dayAfter(day: string): string {
  const [year = 0, month = 1, date = 1] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date + 1))
    .toISOString()
    .slice(0, 10);
}

/**
 * One end of a date range as an instant, in milliseconds, or null when the
 * value is no date (#518): what two ends are compared by to tell a range that
 * runs backwards.
 *
 * `edge` is which end of a range the value is. A day with no time is the whole
 * of that day in the store's zone, so as the start of a range it is the first
 * moment of it and as the end the last: "between 1 Oct and 1 Oct" runs forwards,
 * and "between 1 Oct 10:00 and 1 Oct" does too. A zone-less time is the store's
 * wall clock (#519); anything else is read as the instant it says.
 */
export function dateBoundMs(
  text: string,
  edge: "start" | "end",
  timeZone: string = STORE_TIMEZONE,
): number | null {
  if (isDayOnly(text)) {
    if (!isCalendarDay(text)) return null;
    const start = zonelessInstant(`${text}T00:00`, timeZone);
    if (start === null || edge === "start") return start;
    const next = zonelessInstant(`${dayAfter(text)}T00:00`, timeZone);
    return next === null ? null : next - 1;
  }

  // Null for an impossible one rather than whatever `Date.parse` rolls it to.
  if (looksZoneless(text)) return zonelessInstant(text, timeZone);

  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
}
